import { Code, ConnectError } from "@connectrpc/connect";
import { createGrpcWebTransport } from "@connectrpc/connect-web";
import i18n from "i18next";
import { toast } from "sonner";
import { silentRefresh, useAuthStore } from "./auth";
import { useLocaleStore } from "./locale";
import { useWorkspaceStore } from "./workspace";

const GRPC_CODE_KEYS: Record<number, string> = {
	1: "grpc_cancelled",
	3: "grpc_invalid",
	5: "grpc_not_found",
	6: "grpc_already_exists",
	7: "grpc_no_access",
	9: "grpc_precondition",
	13: "grpc_internal",
	14: "grpc_unavailable",
	16: "grpc_unauthenticated",
};

function friendlyMessage(err: ConnectError): string {
	if (err.rawMessage) return err.rawMessage;
	const key = GRPC_CODE_KEYS[err.code];
	return key ? i18n.t(key) : i18n.t("error_generic");
}

function applyHeaders(req: { header: Headers }) {
	const token = useAuthStore.getState().token;
	if (token) {
		req.header.set("authorization", `Bearer ${token}`);
	}
	const wsId = useWorkspaceStore.getState().selectedId;
	if (wsId) {
		req.header.set("x-workspace-id", wsId);
	}
	req.header.set("accept-language", useLocaleStore.getState().language);
}

const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:50051";

export const transport = createGrpcWebTransport({
	baseUrl,
	interceptors: [
		(next) => async (req) => {
			applyHeaders(req);
			try {
				return await next(req);
			} catch (err) {
				// On unauthenticated, attempt a silent refresh and retry once
				if (err instanceof ConnectError && err.code === Code.Unauthenticated) {
					const refreshed = await silentRefresh();
					if (refreshed) {
						applyHeaders(req);
						return await next(req);
					}
				}

				if (err instanceof ConnectError) {
					const msg = friendlyMessage(err);
					toast.error(msg, { id: `grpc-${err.code}-${msg}` });
				} else if (
					!(err instanceof DOMException && err.name === "AbortError")
				) {
					toast.error(i18n.t("error_server"), { id: "grpc-server-error" });
				}
				throw err;
			}
		},
	],
});

/** Transport with auth but no toast — for calls where components handle errors themselves. */
export const silentTransport = createGrpcWebTransport({
	baseUrl,
	interceptors: [
		(next) => async (req) => {
			applyHeaders(req);
			try {
				return await next(req);
			} catch (err) {
				if (err instanceof ConnectError && err.code === Code.Unauthenticated) {
					const refreshed = await silentRefresh();
					if (refreshed) {
						applyHeaders(req);
						return await next(req);
					}
				}
				throw err;
			}
		},
	],
});
